/**
 * demoData.js
 * Guest Demo Mode Data (Rich Experience Ver.)
 * Ported from legacy/demo-data.js
 */

export const DEMO_CURRICULUM = {
    topic: "Python 기초: 변수와 데이터",
    curriculum: [ // Renamed 'chapters' to 'curriculum' to match React store structure
        {
            step: 1,
            title: "1단원: 데이터 담는 상자, 변수",
            description: "데이터에 이름을 붙이고 관리하는 방법을 시각적으로 배웁니다.",
            stage: "CONCEPT",
            interactive: true
        },
        {
            step: 2,
            title: "2단원: 자료형의 종류",
            description: "문자열, 숫자, 불리언 등 데이터의 타입을 이해합니다.",
            stage: "CONCEPT",
            interactive: false,
            locked: true
        },
        {
            step: 3,
            title: "3단원: 데이터 연산과 가공",
            description: "변수를 더하고, 빼고, 합치는 방법을 실습합니다.",
            stage: "PRACTICE",
            interactive: false,
            locked: true
        }
    ]
};

export const DEMO_CONVERSATIONS = [
    {
        role: 'ai',
        content: '안녕하세요! 저는 AI 코딩 튜터 루나예요. 😊\n무엇을 배우고 싶으신가요?',
        delay: 0
    },
    {
        role: 'user',
        content: 'Python 기초를 배우고 싶어요!',
        delay: 1500
    },
    {
        role: 'ai',
        content: '좋아요! Python은 배우기 쉬운 언어예요. 📚\n먼저 "변수"라는 핵심 개념부터 시작해볼까요?',
        delay: 2500
    },
    {
        role: 'ai',
        content: '커리큘럼을 생성했어요. \n\n오른쪽의 **[샘플 레슨 시작하기]** 버튼을 눌러 바로 체험해보세요! 👉',
        delay: 4000
    }
];

export const DEMO_LESSON = {
    title: "데이터 담는 상자, 변수",
    language: "python",
    steps: [
        {
            type: "context",
            text: "📖 **변수(Variable)**가 왜 필요할까요? 상상을 해봐요! ✨\n\n여러분이 거대한 물류 창고에서 일한다고 생각해보세요. 수천 개의 물건이 쏟아지는데, 상자에 아무런 라벨(이름표)이 없다면 어떨까요?\n\n\"어... 그 빨간색 무거운 거 가져와!\" 라고 하면 찾을 수 없겠죠? 😅\n\n프로그래밍도 똑같아요! 데이터에 **'이름표(변수명)'**를 붙여주지 않으면 나중에 다시 사용할 수 없답니다. 오늘 우리는 데이터에 예쁜 이름표를 붙이는 방법을 배울 거예요! 🚀"
        },
        {
            type: "visual_logic",
            text: "📊 **변수의 원리**를 그림으로 한눈에 볼까요?\n\n우리가 `name = 'Luna'`라고 코드를 작성하면 컴퓨터 내부에서는 이런 일이 일어나요.\n\n1. `'Luna'`라는 데이터를 메모리 어딘가에 만들어요.\n2. 그리고 `name`이라는 이름표(변수)가 그 데이터를 가리키게 되죠.\n\n즉, 변수는 **데이터를 담는 상자** 혹은 **데이터를 가리키는 화살표**라고 생각하면 쉬워요! 💡",
            mermaid_code: `graph LR
    A["내용물: 'Luna'"] 
    B{{"상자 이름: name"}}
    C["메모리 공간"]
    
    B -->|"대입 (=)"| A
    A --- C
    style B fill:#22d3ee,stroke:#0891b2,color:#000
    style A fill:#a78bfa,stroke:#7c3aed,color:#000`
        },
        {
            type: "master_pattern",
            text: "🗝️ **핵심 공식**이에요! 꼭 기억하세요 ✨\n\n프로그래밍에서 `=` 기호는 '같다'는 뜻이 아니라 **'오른쪽 값을 왼쪽에 담는다(대입)'**는 뜻이에요!\n\n마치 `[이름표] ← [내용물]` 처럼요.\n이 패턴만 알면 어떤 데이터든 저장할 수 있어요! 😊",
            code_template: `# 변수 만들기 공식
variable_name = value  # 변수명 = 저장할값

# 예시
my_age = 25
print(my_age)`
        },
        {
            type: "mirror_example",
            text: "🪞 패턴을 이렇게 응용할 수 있어요!\n\n숫자뿐만 아니라 **문자열(텍스트)**도 담을 수 있죠.\n단, 문자는 반드시 **따옴표('', \"\")**로 감싸줘야 한다는 점, 잊지 마세요! 😉",
            code_example: `# 자기소개 데이터 저장하기
name = "Iron Man"
job = "Hero"

print(name)
print(job)`
        },
        {
            type: "mcq",
            text: "자, 잘 이해했는지 확인해볼까요? 👀",
            question: "다음 중 변수에 값을 올바르게 저장한 코드는 무엇일까요?",
            code_snippet: `1. name = "Luna"
2. "Luna" = name
3. name == "Luna"
4. print("Luna")`,
            options: [
                "1번: name = \"Luna\"",
                "2번: \"Luna\" = name",
                "3번: name == \"Luna\"",
                "4번: print(\"Luna\")"
            ],
            correct_answer_index: 0,
            feedback: "정답이에요! 🎉 변수명은 항상 왼쪽, 값은 오른쪽에 와야 해요. (`변수 = 값`)"
        },
        {
            type: 'code_fx',
            title: '✨ 인터랙티브 실습: 날씨 데이터 조작',
            desc: '변수 값을 수정하여 오늘의 날씨를 바꿔보세요! 코드를 수정하면 화면이 실시간으로 반응합니다.',
            initialCode: `# 날씨 API의 응답 데이터를 설정합니다.
temperature = 25  # 온도 (섭씨)
condition = "Sunny" # "Sunny", "Cloudy", "Snowy"
humidity = 40     # 습도 (%)`,
            visual_template: 'weather_card',
            extractions: [
                { name: 'temperature', regex: /temperature\s*=\s*(-?\d+)/, default: '25' },
                { name: 'condition', regex: /condition\s*=\s*["'](.*?)["']/, default: 'Sunny' },
                { name: 'humidity', regex: /humidity\s*=\s*(\d+)/, default: '40' }
            ]
        },
        {
            type: 'terminal_sim',
            title: '💻 터미널 시뮬레이션: 서버 구동',
            desc: '포트 번호를 설정하고 서버를 실행해보세요. 실제 터미널처럼 로그가 출력됩니다.',
            initialCode: `from flask import Flask
app = Flask(__name__)

# 포트 번호를 8080으로 변경해보세요!
port = 5000

if __name__ == '__main__':
    app.run(port=port)`,
            regex: /port\s*=\s*(\d+)/,
            logs: [
                { t: " * Serving Flask app 'weather_api'", c: "text-slate-400", d: 0 },
                { t: " * Debug mode: on", c: "text-slate-400", d: 200 },
                { t: " * Running on http://127.0.0.1:{val}", c: "text-green-400", highlight: true, d: 600 },
                { t: " * Press CTRL+C to quit", c: "text-slate-500", d: 800 }
            ]
        },
        {
            type: 'data_grid',
            title: '📊 데이터 그리드: 분석 및 정렬',
            desc: "수집된 도시별 데이터를 원하는 컬럼 기준으로 정렬해보세요. 컬럼명을 정확히 입력해야 해요!",
            initialCode: `import pandas as pd

data = [
    {'City': 'Seoul', 'Temp': 28, 'Hum': 60},
    {'City': 'Tokyo', 'Temp': 32, 'Hum': 70},
    {'City': 'London', 'Temp': 15, 'Hum': 80},
    {'City': 'Moscow', 'Temp': -5, 'Hum': 40}
]
df = pd.DataFrame(data)

# 아래 코드를 완성하여 데이터를 정렬해보세요.
# 힌트: df = df.sort_values(by='컬럼명')을 사용합니다.
`,
            regex: /sort_values\s*\(\s*by\s*=\s*['"](.*?)['"]\s*\)/,
            mockData: [
                { City: 'Seoul', Temp: 28, Hum: 60 },
                { City: 'Tokyo', Temp: 32, Hum: 70 },
                { City: 'London', Temp: 15, Hum: 80 },
                { City: 'Moscow', Temp: -5, Hum: 40 }
            ]
        },
        {
            type: 'safe_runner',
            title: '🛡️ 안전 장치: 무한 루프 방어',
            desc: '무한 루프를 실행하면 안전장치가 자동으로 차단합니다. break를 추가해서 직접 멈춰보세요!',
            initialCode: `count = 0
print("데이터 수집 시작...")

# 아래 코드를 실행하면 무한 루프가 발생합니다.
# break를 추가하여 안전하게 멈춰보세요!
while True:
    count += 1
    print(f"[{count}회차] 데이터 수집 완료")`,
            extraction: { regex: 'count\\s*>=\\s*(\\d+)' },
            safety_limits: { max_iterations: 20 }
        },
        {
            type: "final_code",
            title: "오늘의 미션! 나만의 명함 만들기",
            text: "🎯 **오늘의 미션!** 나만의 AI 명함을 만들어보세요 ✨\n\n여러분의 이름과 직업을 변수에 저장하여 명함을 완성해보세요. 코드를 직접 수정하고 **[실행]** 버튼을 눌러 결과를 확인하세요!",
            problem_json: {
                language: "python",
                title: "나만의 명함 만들기",
                description: "변수 `name`, `job`, `email`에 여러분의 정보를 저장하여 명함을 완성하세요.\n- 세 변수 모두 빈 문자열(`\"\"`)이 아닌 실제 값을 넣어주세요!\n- 문자열은 따옴표로 감싸는 것 잊지 마세요!",
                code_template: `# 1. 변수에 여러분의 정보를 저장하세요
name = ""     # 예: "홍길동"
job = ""      # 예: "개발자"
email = ""    # 예: "hello@luna.com"

# 2. 정보를 출력합니다
print("=== 명함 ===")
print("이름:", name)
print("직업:", job)
print("이메일:", email)
print("===========")`,
                examples: [
                    {
                        input: "name = \"홍길동\", job = \"개발자\", email = \"hong@luna.com\"",
                        output: "=== 명함 ===\n이름: 홍길동\n직업: 개발자\n이메일: hong@luna.com\n==========="
                    }
                ]
            }
        }
    ]
};
